/**
 * What an app's window holds besides its name: the connection itself. The AI account (a Grok plan,
 * signed in with a code on x.ai), the brokers (Robinhood signs in on its own site; IBKR's login is
 * sealed on the server and approved in IBKR Mobile), Telegram, and the browsers that read pages for
 * the agents.
 */
import { useCallback, useEffect, useState, type ReactNode } from 'react'
import type { BrokerMode, BrokerStatus, GatewayPhase } from '@shared/broker'
import { useBrokerStream } from '../hooks/useBrokerStream'
import { Pick } from './Agents'
import { api, brokerApi, robinhoodApi, xaiApi, type BridgeDevice, type RobinhoodStatus, type XaiLoginView, type XaiSubscriptionStatus } from './api'
import { Btn, Status, Switch, askSure, cx, inputCls } from './kit'
import { Row, Rows, note, quiet, value } from './rows'
import { useDemo } from './store'

const when = (t: number) => new Date(t).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
const clock = (ms: number) => {
  const s = Math.max(0, Math.round(ms / 1000))
  return `${Math.floor(s / 60)}:${`${s % 60}`.padStart(2, '0')}`
}
const say = (m: string) => useDemo.getState().say(m)
const fail = (e: unknown) => say(e instanceof Error ? e.message : 'That did not work')
const Part = ({ children }: { children: ReactNode }) => <div className="mt-4 space-y-3">{children}</div>
const Actions = ({ children }: { children: ReactNode }) => <div className="flex flex-wrap items-center gap-2">{children}</div>
const copy = (text: string, done: string) => void navigator.clipboard.writeText(text).then(() => say(done), () => say(text))

/* ── the AI account ──────────────────────────────────────────────────── */

/** A Grok plan pays for the agents. Signing in is a code approved on x.ai; the server keeps what it gets back. */
export function GrokPart({ onChanged }: { onChanged?: () => void }) {
  const admin = useDemo((s) => s.user?.role === 'admin')
  const [st, setSt] = useState<XaiSubscriptionStatus | null>(null)
  const [login, setLogin] = useState<XaiLoginView | null>(null)
  const [busy, setBusy] = useState(false)
  const [now, setNow] = useState(() => Date.now())
  const load = useCallback(async () => {
    try {
      const s = await xaiApi.status()
      setSt(s)
      if (s.pending) setLogin(s.pending)
    } catch (e) {
      fail(e)
    }
  }, [])
  useEffect(() => void load(), [load])
  // While a sign-in is open the server asks xAI how it went; this only asks the server.
  const pending = login?.state === 'pending' ? login.sessionId : null
  useEffect(() => {
    if (!pending) return
    const every = Math.min(Math.max(login?.interval ?? 5, 2), 5) * 1000
    const t = window.setInterval(async () => {
      try {
        const v = await xaiApi.poll(pending)
        if (v.state === 'pending') return
        setLogin(null)
        if (v.state === 'connected') say('Connected — your agents run on your Grok plan')
        else if (v.state !== 'cancelled') say(v.message ?? (v.state === 'denied' ? 'The sign-in was declined' : 'The code ran out — start again'))
        await load()
        onChanged?.()
      } catch {
        /* keep asking */
      }
    }, every)
    const tick = window.setInterval(() => setNow(Date.now()), 1000)
    return () => (window.clearInterval(t), window.clearInterval(tick))
  }, [pending, login?.interval, load, onChanged])
  const start = async () => {
    setBusy(true)
    try {
      setLogin(await xaiApi.start())
      setNow(Date.now())
    } catch (e) {
      fail(e)
    } finally {
      setBusy(false)
    }
  }
  const known = !!st && st.status !== 'not_connected'
  const state = !st ? '' : st.status === 'needs_login' ? 'Needs to be signed in again' : st.status === 'tier_denied' ? 'This plan cannot be used here' : 'Active'
  return (
    <Part>
      {login?.state === 'pending' ? (
        <div className="rounded-[8px] border border-ws-line px-3 py-3">
          <div className="text-[12px] leading-[18px] tracking-normal text-ws-fg-3">Open x.ai, sign in with the account that has the plan, and approve this code:</div>
          <div className="mt-2 flex items-center gap-2">
            <span className="select-all rounded-[6px] bg-ws-field px-3 py-[6px] text-[20px] font-medium leading-7 tracking-[0.16em] tabular-nums text-ws-fg">{login.userCode}</span>
            <button type="button" className={quiet} onClick={() => copy(login.userCode, 'Code copied')}>
              Copy
            </button>
          </div>
          <div className="mt-3 flex items-center gap-2">
            <a href={login.verificationUrlComplete} target="_blank" rel="noopener noreferrer" className="flex h-8 items-center rounded-[8px] bg-white px-3 text-[13px] font-medium tracking-normal text-black">
              Open x.ai to approve
            </a>
            <Btn variant="outline" onPanel className="px-3" onClick={() => void xaiApi.cancel(login.sessionId).catch(() => {}).then(() => setLogin(null))}>
              Cancel
            </Btn>
          </div>
          <div className={cx(note, 'animate-ws-pulse')}>Waiting for your approval · the code is good for {clock(login.expiresAt - now)}</div>
          <div className="mt-1 text-[12px] leading-[18px] tracking-normal text-ws-fg-4">xAI’s page may call the app “Grok Build”: that is the name of the sign-in this uses.</div>
        </div>
      ) : (
        known && (
          <Rows>
            {st!.email && <Row title="Account" right={<span className={value}>{st!.email}</span>} />}
            <Row title="State" sub={st!.lastError && !st!.connected ? st!.lastError : undefined} right={<Status tone={st!.connected && st!.status === 'active' ? 'free' : 'down'}>{state}</Status>} />
            {st!.week && <Row title="Last 7 days" sub={st!.week.subscription.costUsd > 0 ? `About $${st!.week.subscription.costUsd.toFixed(2)} of use covered by the plan` : undefined} right={<span className={value}>{st!.week.subscription.runs} on the plan{admin ? ` · ${st!.week.api.runs} on the API key` : ''}</span>} />}
            {admin && <Row title="Go on with the API key when the plan runs out" sub="An answer never stops halfway; the switch is noted under it" right={<Switch on={st!.fallbackToApi} onChange={(v) => (setSt({ ...st!, fallbackToApi: v }), void xaiApi.setFallback(v).catch(fail))} label="Go on with the API key when the plan runs out" />} />}
          </Rows>
        )
      )}
      {!login && (
        <Actions>
          {(!st?.connected || st.status !== 'active') && (
            <Btn variant="white" className="px-3" disabled={busy || !st} onClick={() => void start()}>
              {known ? 'Sign in again' : 'Connect your Grok plan'}
            </Btn>
          )}
          {known && (
            <Btn
              variant="outline"
              onPanel
              className="px-3"
              disabled={busy}
              onClick={() =>
                askSure({
                  title: 'Disconnect your Grok plan?',
                  body: admin ? 'Your agents go back to the server’s API key, paid by use.' : 'Your agents cannot answer until a plan is connected again.',
                  cta: 'Disconnect',
                  done: () => void xaiApi.disconnect().then(() => (say('Disconnected'), load(), onChanged?.()), fail),
                })
              }
            >
              Disconnect
            </Btn>
          )}
        </Actions>
      )}
      {!known && !login && <p className={note}>{admin ? 'Run your agents on your own Grok plan (SuperGrok or X Premium+) instead of the server’s API key.' : 'The agents here run on your own Grok plan (SuperGrok or X Premium+). Connect it to talk to them and to let routines run.'}</p>}
    </Part>
  )
}

/* ── Robinhood ───────────────────────────────────────────────────────── */

export function RobinhoodPart({ onChanged }: { onChanged?: () => void }) {
  const [st, setSt] = useState<RobinhoodStatus | null>(null)
  const [busy, setBusy] = useState(false)
  const [tools, setTools] = useState<{ name: string; description: string }[] | null>(null)
  // The sign-in address while one is open: you approve there, then paste the address it ends on.
  const [signIn, setSignIn] = useState<string | null>(null)
  const [pasted, setPasted] = useState('')
  const load = useCallback(() => robinhoodApi.status().then(setSt, fail), [])
  useEffect(() => void load(), [load])
  const run = async (job: () => Promise<unknown>) => {
    setBusy(true)
    try {
      await job()
    } catch (e) {
      fail(e)
    } finally {
      setBusy(false)
      await load()
      onChanged?.()
    }
  }
  const again = st?.status === 'needs_login'
  return (
    <Part>
      {(st?.connected || again) && (
        <Rows>
          <Row title="State" sub={st!.lastError ?? undefined} right={<Status tone={st!.connected ? 'free' : 'down'}>{st!.connected ? 'Active' : 'Needs to be signed in again'}</Status>} />
          {st!.connected && (
            <Row
              title="Trading"
              sub={st!.session === 'connected' ? (st!.tradingBlockedReason ?? `Agentic account ${st!.account ? `··${st!.account.slice(-4)}` : ''} · real money`) : undefined}
              right={<Status tone={st!.session === 'connected' && !st!.tradingBlockedReason ? 'free' : st!.session === 'connecting' ? 'warn' : 'off'}>{st!.session === 'connected' ? (st!.tradingBlockedReason ? 'Held back' : 'Ready') : st!.session === 'connecting' ? 'Connecting' : 'Not running'}</Status>}
            />
          )}
          {st!.connectedAt && <Row title="Connected" right={<span className={value}>{when(st!.connectedAt)}</span>} />}
          {st!.toolCount != null && <Row title="What Robinhood lets an agent do" right={<span className={value}>{st!.toolCount} functions</span>} />}
        </Rows>
      )}
      {tools && (
        <div className="max-h-56 overflow-y-auto rounded-[8px] border border-ws-line px-3 py-2">
          {tools.map((t) => (
            <div key={t.name} className="py-1 text-[12px] leading-[17px] tracking-normal">
              <span className="font-mono text-ws-fg-2">{t.name}</span>
              {t.description && <span className="text-ws-fg-4"> — {t.description}</span>}
            </div>
          ))}
        </div>
      )}
      {signIn && !st?.connected && (
        <div className="rounded-[8px] border border-ws-line px-3 py-3 text-[12px] leading-[18px] tracking-normal text-ws-fg-3">
          <ol className="m-0 list-decimal space-y-1 pl-4">
            <li>
              Sign in and approve on Robinhood (
              <a href={signIn} target="_blank" rel="noopener noreferrer" className="text-ws-fg-2 underline decoration-ws-rule underline-offset-2 hover:text-ws-fg">
                open the page again
              </a>
              ).
            </li>
            <li>
              The browser then lands on a page that cannot be opened; its address starts with <span className="font-mono text-ws-fg-2">http://127.0.0.1:8765</span>. That is expected.
            </li>
            <li>Copy that page’s whole address and paste it here.</li>
          </ol>
          <div className="mt-3 flex gap-2">
            <input value={pasted} onChange={(e) => setPasted(e.target.value)} placeholder="http://127.0.0.1:8765/oauth/callback?code=…" spellCheck={false} autoComplete="off" className={cx(inputCls, 'font-mono text-[12px]')} />
            <Btn variant="white" className="shrink-0 px-3" disabled={busy || !pasted.trim()} onClick={() => void run(() => robinhoodApi.finish(pasted).then(() => (setSignIn(null), setPasted(''), say('Robinhood is connected'))))}>
              Finish
            </Btn>
          </div>
        </div>
      )}
      <Actions>
        {!st?.connected ? (
          <Btn variant="white" className="px-3" disabled={busy || !st} onClick={() => void run(() => robinhoodApi.start().then(({ url }) => (setSignIn(url), void window.open(url, '_blank', 'noopener'))))}>
            {again ? 'Sign in to Robinhood again' : 'Connect Robinhood'}
          </Btn>
        ) : (
          <>
            <Btn variant="outline" onPanel className="px-3" disabled={busy} onClick={() => void run(() => robinhoodApi.tools().then((r) => setTools(r.tools)))}>
              {busy ? 'Checking' : 'Check the connection'}
            </Btn>
            <Btn variant="outline" onPanel className="px-3" disabled={busy} onClick={() => askSure({ title: 'Disconnect Robinhood?', body: 'The sign-in is deleted from this server. Workers that run there stop being minded; their stops already resting at Robinhood stay.', cta: 'Disconnect', done: () => void run(() => robinhoodApi.disconnect().then(() => (setTools(null), say('Disconnected')))) })}>
              Disconnect
            </Btn>
          </>
        )}
      </Actions>
      {!st?.connected && !signIn && <p className={note}>You sign in on robinhood.com; your password is never typed here. Agents trade only in your Agentic account, and every order waits for your approval.</p>}
    </Part>
  )
}

/* ── IBKR ────────────────────────────────────────────────────────────── */

const PHASE: Record<GatewayPhase, { tone: 'free' | 'warn' | 'off' | 'down'; text: string }> = {
  absent: { tone: 'off', text: 'Not made yet' },
  stopped: { tone: 'off', text: 'Stopped' },
  starting: { tone: 'warn', text: 'Starting, signing in' },
  awaiting_2fa: { tone: 'warn', text: 'Waiting for you in IBKR Mobile' },
  running: { tone: 'free', text: 'Running' },
  login_failed: { tone: 'down', text: 'The login was refused' },
  error: { tone: 'down', text: 'Something went wrong' },
}

export function IbkrPart() {
  const { snapshot } = useBrokerStream()
  const [seen, setSeen] = useState<BrokerStatus | null>(null)
  const status = snapshot?.status ?? seen
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [mode, setMode] = useState<BrokerMode>('paper')
  const [busy, setBusy] = useState(false)
  const run = async (job: () => Promise<BrokerStatus>, done: string) => {
    setBusy(true)
    try {
      setSeen(await job())
      say(done)
    } catch (e) {
      fail(e)
    } finally {
      setBusy(false)
    }
  }
  const cred = status?.credentials
  const gw = status?.gateway
  const up = !!gw && gw.phase !== 'absent' && gw.phase !== 'stopped'
  return (
    <Part>
      {status && (
        <Rows>
          <Row title="Login" sub={cred?.configured ? `${cred.usernameHint ?? ''} · ${cred.mode === 'live' ? 'a real account' : 'a paper account'}${cred.enabled ? '' : ' · paused'}` : 'Nothing is stored'} right={<Status tone={cred?.configured ? 'free' : 'off'}>{cred?.configured ? 'Sealed on the server' : 'None'}</Status>} />
          <Row title="Gateway" sub="The program that holds the session with IBKR" right={<Status tone={gw ? PHASE[gw.phase].tone : 'off'}>{gw ? PHASE[gw.phase].text : '—'}</Status>} />
          <Row title="Session" right={<Status tone={status.connection === 'connected' ? 'free' : status.connection === 'connecting' ? 'warn' : 'off'}>{status.connection === 'connected' ? 'Connected' : status.connection === 'connecting' ? 'Connecting' : 'Not connected'}</Status>} />
          {status.accounts.length > 0 && <Row title="Account" sub={status.isPaper ? 'Paper: no real money' : 'Real money'} right={<span className={value}>{status.accounts.map((a) => `··${a.slice(-4)}`).join(', ')}</span>} />}
          <Row title="Orders" sub={status.tradingEnabled ? undefined : (status.tradingBlockedReason ?? undefined)} right={<Status tone={status.tradingEnabled ? 'free' : 'off'}>{status.tradingEnabled ? 'Accepted' : 'Held back'}</Status>} />
          {status.lastError && <Row title="Last error" sub={status.lastError} />}
        </Rows>
      )}
      <div className="space-y-2">
        <input value={username} onChange={(e) => setUsername(e.target.value)} placeholder={cred?.configured ? 'IBKR username — type to replace' : 'IBKR username'} autoComplete="off" spellCheck={false} className={inputCls} aria-label="IBKR username" />
        <input value={password} onChange={(e) => setPassword(e.target.value)} type="password" placeholder={cred?.configured ? 'A password is stored — type to replace' : 'IBKR password'} autoComplete="new-password" className={inputCls} aria-label="IBKR password" />
        <Pick options={status?.liveAllowed ? ['Paper', 'Real money'] : ['Paper']} value={mode === 'live' ? 'Real money' : 'Paper'} onChange={(v) => setMode(v === 'Real money' ? 'live' : 'paper')} />
      </div>
      <Actions>
        <Btn
          variant="white"
          className="px-3"
          disabled={busy || !username.trim() || !password}
          onClick={() =>
            void run(async () => {
              const s = await brokerApi.saveCredentials(username.trim(), password, mode)
              // (what was typed is dropped the moment the server has sealed it)
              setPassword('')
              setUsername('')
              return s
            }, 'Sealed. The gateway is signing in — approve it in IBKR Mobile')
          }
        >
          {busy ? 'Working' : 'Save and connect'}
        </Btn>
        {cred?.configured && (
          <>
            <Btn variant="outline" onPanel className="px-3" disabled={busy} onClick={() => (up ? askSure({ title: 'Start the gateway again?', body: 'The session with IBKR ends and a new one begins: you approve it again in IBKR Mobile.', cta: 'Start again', done: () => void run(brokerApi.startGateway, 'Starting — approve it in IBKR Mobile') }) : void run(brokerApi.startGateway, 'Starting — approve it in IBKR Mobile'))}>
              {up ? 'Start again' : 'Start'}
            </Btn>
            {up && (
              <Btn variant="outline" onPanel className="px-3" disabled={busy} onClick={() => askSure({ title: 'Stop the gateway?', body: 'The session with IBKR ends. Orders already at IBKR stay; workers there are no longer minded.', cta: 'Stop', done: () => void run(brokerApi.stopGateway, 'Stopped') })}>
                Stop
              </Btn>
            )}
            <Btn variant="danger" className="px-3" disabled={busy} onClick={() => askSure({ title: 'Remove the IBKR login?', body: 'The login is deleted from the server and the gateway stops.', cta: 'Remove', done: () => void run(brokerApi.deleteCredentials, 'Removed') })}>
              Remove login
            </Btn>
          </>
        )}
      </Actions>
      <p className={note}>Your login is sealed on the server and never shown again, to you or to an agent. The gateway signs in with it; you approve each sign-in in IBKR Mobile.</p>
    </Part>
  )
}

/* ── Telegram ────────────────────────────────────────────────────────── */

export function TelegramPart() {
  const settings = useDemo((s) => s.settings)
  const save = useDemo((s) => s.saveSettings)
  const [token, setToken] = useState('')
  const [chat, setChat] = useState(settings?.telegramChatId ?? '')
  const [busy, setBusy] = useState(false)
  const has = !!settings?.telegramConfigured
  const run = async (job: () => Promise<unknown>) => {
    setBusy(true)
    try {
      await job()
    } catch (e) {
      fail(e)
    } finally {
      setBusy(false)
    }
  }
  const store = () => save({ telegramChatId: chat.trim(), ...(token.trim() ? { telegramBotToken: token.trim() } : {}) }).then(() => setToken(''))
  const find = async () => {
    if (token.trim()) await save({ telegramBotToken: token.trim() })
    const r = await api.telegramDetectChat()
    setChat(r.chatId)
    await save({ telegramChatId: r.chatId })
    setToken('')
    say(`Found: ${r.name}`)
  }
  return (
    <Part>
      <div className="text-[12px] leading-[18px] tracking-normal text-ws-fg-3">Agents write to you through a bot of your own. Make one with @BotFather in Telegram, paste its token here, then send the bot any message and press Find my chat.</div>
      <label className="block">
        <span className="mb-[6px] block text-[12px] leading-4 tracking-normal text-ws-label">The bot’s token</span>
        <input value={token} onChange={(e) => setToken(e.target.value)} type="password" autoComplete="off" placeholder={has ? 'One is saved — paste a new one to replace it' : '123456:ABC…'} className={inputCls} />
      </label>
      <label className="block">
        <span className="mb-[6px] block text-[12px] leading-4 tracking-normal text-ws-label">Your chat with the bot</span>
        <span className="flex gap-2">
          <input value={chat} onChange={(e) => setChat(e.target.value)} inputMode="numeric" placeholder="Chat ID" className={inputCls} />
          <Btn variant="outline" onPanel className="shrink-0 px-3" disabled={busy || (!has && !token.trim())} onClick={() => void run(find)}>
            Find my chat
          </Btn>
        </span>
      </label>
      <Actions>
        <Btn variant="white" className="px-3" disabled={busy || (!token.trim() && chat.trim() === (settings?.telegramChatId ?? ''))} onClick={() => void run(() => store().then(() => say('Saved')))}>
          Save
        </Btn>
        <Btn variant="outline" onPanel className="px-3" disabled={busy || !has} onClick={() => void run(() => api.telegramTest().then(() => say('A test message is on its way')))}>
          Send a test
        </Btn>
        {has && (
          <Btn variant="danger" className="px-3" disabled={busy} onClick={() => askSure({ title: 'Disconnect Telegram?', body: 'The bot’s token is deleted here. Agents can no longer write to you there.', cta: 'Disconnect', done: () => void run(() => api.telegramDisconnect().then(() => save({})).then(() => (setChat(''), say('Disconnected')))) })}>
            Disconnect
          </Btn>
        )}
      </Actions>
    </Part>
  )
}

/* ── the browsers that read pages for the agents ─────────────────────── */

export function BrowsersPart({ onChanged }: { onChanged?: () => void }) {
  const [devices, setDevices] = useState<BridgeDevice[]>([])
  const [fresh, setFresh] = useState<string | null>(null)
  const [name, setName] = useState('')
  const load = useCallback(() => api.bridgeDevices().then((x) => setDevices(x.devices), () => {}), [])
  useEffect(() => {
    void load()
    const t = window.setInterval(() => void load(), 4000) // (whether each is connected right now)
    return () => window.clearInterval(t)
  }, [load])
  const add = () =>
    void api.bridgeAddDevice(name.trim() || 'My browser').then((d) => {
      setFresh(d.token)
      setName('')
      void load()
      onChanged?.()
    }, fail)
  return (
    <Part>
      <Rows>
        {devices.map((d) => (
          <Row
            key={d.id}
            title={d.label}
            right={
              <span className="flex shrink-0 items-center gap-2">
                <Status tone={d.connected ? 'free' : 'off'}>{d.connected ? 'Connected now' : 'Offline'}</Status>
                <button type="button" className={quiet} onClick={() => askSure({ title: `Remove ${d.label}?`, body: 'Its code stops working. Pair it again to use it.', cta: 'Remove', done: () => void api.bridgeRevokeDevice(d.id).then(() => (load(), onChanged?.()), fail) })}>
                  Remove
                </button>
              </span>
            }
          />
        ))}
        {!devices.length && <Row title="No browser is paired yet" sub="Without one, pages are read from the server" />}
      </Rows>
      {fresh && (
        <div className="rounded-[8px] border border-ws-line px-3 py-3">
          <div className="text-[12px] leading-[18px] tracking-normal text-ws-fg-3">
            Paste this into the extension, with the server address <span className="font-mono text-ws-fg-2">{window.location.origin}</span>. It is shown once.
          </div>
          <div className="mt-2 flex items-center gap-2">
            <code className="min-w-0 flex-1 truncate rounded-[6px] bg-ws-field px-2 py-[6px] font-mono text-[12px] tracking-normal text-ws-fg">{fresh}</code>
            <button type="button" className={quiet} onClick={() => copy(fresh, 'Code copied')}>
              Copy
            </button>
            <button type="button" className={quiet} onClick={() => setFresh(null)}>
              Done
            </button>
          </div>
        </div>
      )}
      <div className="flex gap-2">
        <input value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && add()} placeholder="A name for it, e.g. the computer at home" className={inputCls} aria-label="A name for the browser" />
        <Btn variant="white" className="shrink-0 px-3" onClick={add}>
          Pair a browser
        </Btn>
      </div>
      <p className={note}>Install the Liberabank extension in a browser and pair it here. Pages are then read through that browser, from its own address — sites that turn a server away let it in. Leave one running at home and every device uses it.</p>
    </Part>
  )
}
